import { Sprite, Text, Texture } from 'pixi.js';
import { Scene } from '../engine/scene';
import { getContext } from '../engine/context';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/tuning';
import { HighScoreStore, type HighScoreMap } from '../systems/HighScoreStore';
import type { Difficulty } from '../config/difficulty';

interface HighScoreData {
  from?: string;
}

export class HighScoreScene extends Scene {
  private fromScene = 'MenuScene';

  async onEnter(data?: unknown): Promise<void> {
    this.fromScene = (data as HighScoreData)?.from ?? 'MenuScene';
    const ctx = getContext();
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;

    // Centered panel
    const panel = new Sprite(Texture.from('ui_high_score_panel'));
    panel.anchor.set(0.5);
    panel.position.set(cx, cy);
    this.addChild(panel);

    const scores = HighScoreStore.read();
    const panelTopY = cy - 250;
    const rowOffset = 160;
    const rowSpacing = 95;

    this.renderRow('easy', scores, panelTopY + rowOffset);
    this.renderRow('medium', scores, panelTopY + rowOffset + rowSpacing);
    this.renderRow('hard', scores, panelTopY + rowOffset + rowSpacing * 2);
    this.renderRow('narco', scores, panelTopY + rowOffset + rowSpacing * 3);

    const back = new Text({
      text: '‹ Back',
      style: {
        fontFamily: 'Impact, sans-serif',
        fontSize: 28,
        fill: 0x5fdbcf,
      },
    });
    back.anchor.set(0.5);
    back.position.set(cx, GAME_HEIGHT - 60);
    back.eventMode = 'static';
    back.cursor = 'pointer';
    back.on('pointerover', () => { back.style.fill = 0xffd23f; });
    back.on('pointerout', () => { back.style.fill = 0x5fdbcf; });
    back.on('pointerdown', () => {
      ctx.audio.play('sfx_click', { volume: 0.55 });
      ctx.sceneManager.start(this.fromScene);
    });
    this.addChild(back);
  }

  private renderRow(diff: Difficulty, scores: HighScoreMap, y: number): void {
    const entry = scores[diff];
    const colorByDiff: Record<Difficulty, number> = {
      easy: 0x79e8a3,
      medium: 0xffd23f,
      hard: 0xff6b35,
      narco: 0xff1a55,
    };

    const cx = GAME_WIDTH / 2;
    const colLeft = cx - 260;
    const col1 = cx - 80;
    const col2 = cx + 60;
    const col3 = cx + 200;

    this.addText(colLeft, y, diff.toUpperCase(), {
      fontFamily: 'Impact, sans-serif', fontSize: 34, fill: colorByDiff[diff],
    }, [0, 0.5]);

    const smallLabel = { fontFamily: 'Inter, sans-serif', fontSize: 13, fill: 0x88889a };
    const valueStyle = { fontFamily: 'Impact, sans-serif', fontSize: 22, fill: 0xffffff };

    this.addText(col1, y - 14, 'Best total', smallLabel, [0, 0.5]);
    this.addText(col1, y + 8, `${entry.longestMatchTotal.toFixed(2)}s`, valueStyle, [0, 0.5]);

    this.addText(col2, y - 14, 'Best drag', smallLabel, [0, 0.5]);
    this.addText(col2, y + 8, `${entry.longestSingleDrag.toFixed(2)}s`, valueStyle, [0, 0.5]);

    this.addText(col3, y - 14, 'Wins', smallLabel, [0, 0.5]);
    this.addText(col3, y + 8, `${entry.matchesWon}/${entry.matchesPlayed}`, valueStyle, [0, 0.5]);
  }

  private addText(
    x: number,
    y: number,
    text: string,
    style: { fontFamily: string; fontSize: number; fill: number },
    anchor: [number, number] = [0, 0.5]
  ): void {
    const t = new Text({ text, style });
    t.anchor.set(anchor[0], anchor[1]);
    t.position.set(x, y);
    this.addChild(t);
  }
}
